import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, MapPin, Wallet, Layers, Loader, ListChecks, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import { participationsAPI, handleApiError } from '../utils/api';

const formatEuros = (value) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(value || 0);

const formatDay = (isoDate) =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

const todayIso = () => new Date().toLocaleDateString('sv-SE'); // AAAA-MM-JJ, date locale

/**
 * Récapitulatif des tournois à venir auxquels l'utilisateur est inscrit :
 * classés par jour, avec heure, lieu, buy-in, et un budget estimé.
 */
const MySchedulePage = () => {
  const navigate = useNavigate();
  const [participations, setParticipations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await participationsAPI.getMine();
        if (!cancelled) setParticipations(response.data || []);
      } catch (err) {
        const { error: errTitle, details } = handleApiError(err);
        if (!cancelled) setError(`${errTitle} : ${details}`);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const upcoming = useMemo(() => {
    const today = todayIso();
    return participations
      .filter((p) => p.status !== 'CANCELLED' && p.tournamentDate && p.tournamentDate >= today)
      .sort((a, b) =>
        a.tournamentDate === b.tournamentDate
          ? String(a.tournamentTime || '').localeCompare(String(b.tournamentTime || ''))
          : a.tournamentDate.localeCompare(b.tournamentDate)
      );
  }, [participations]);

  const days = useMemo(() => {
    const map = new Map();
    upcoming.forEach((p) => {
      if (!map.has(p.tournamentDate)) map.set(p.tournamentDate, []);
      map.get(p.tournamentDate).push(p);
    });
    return Array.from(map.entries());
  }, [upcoming]);

  // Budget estimé : buy-in du tournoi x nombre d'entrées prévues (1 à défaut),
  // pour les tournois qui ont leur propre buy-in (pas un Final Day atteint
  // sans nouvelle inscription) et dont le montant est connu.
  const budget = useMemo(() => {
    let total = 0;
    let unknown = 0;
    upcoming.forEach((p) => {
      if (p.hasOwnBuyIn === false) return;
      if (p.tournamentBuyInAmount == null) {
        unknown += 1;
        return;
      }
      const entries = p.entriesCount >= 1 ? p.entriesCount : 1;
      total += Number(p.tournamentBuyInAmount) * entries;
    });
    return { total, unknown };
  }, [upcoming]);

  const next = upcoming[0];

  const renderRow = (p) => {
    const buyIn =
      p.hasOwnBuyIn === false
        ? 'Sans inscription propre'
        : p.tournamentBuyInAmount != null
          ? formatEuros(p.tournamentBuyInAmount)
          : null;
    return (
      <li key={p.id}>
        <button
          onClick={() => navigate(`/event/${p.festivalId}/subevent/${p.tournamentId}`)}
          className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition"
        >
          <div className="w-14 flex-shrink-0 text-lg font-bold text-blue-600">
            {String(p.tournamentTime || '').slice(0, 5) || '—'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-800 break-words">{p.tournamentTitle}</p>
            <p className="text-sm text-gray-500">
              {p.festivalTitle}
              {p.seriesName && (
                <span className="ml-2 inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                  <Layers size={12} />
                  Série
                </span>
              )}
            </p>
            <p className="text-sm text-gray-500 flex flex-wrap items-center gap-x-3">
              {p.venueName && (
                <span className="inline-flex items-center gap-1">
                  <MapPin size={14} />
                  {p.venueName}
                </span>
              )}
              {buyIn && <span>Buy-in : {buyIn}</span>}
              {p.entriesCount >= 2 && <span>{p.entriesCount} entrées prévues</span>}
            </p>
          </div>
          <ChevronRight size={18} className="text-gray-400 flex-shrink-0 mt-1" />
        </button>
      </li>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center">
              <CalendarClock className="mr-3" size={30} />
              Mon programme
            </h1>
            <button
              onClick={() => navigate('/my-participations')}
              className="text-blue-600 hover:underline flex items-center gap-1 text-sm"
            >
              <ListChecks size={16} />
              Mes participations et résultats
            </button>
          </div>

          {isLoading && (
            <div className="text-center text-gray-500 py-12">
              <Loader className="animate-spin inline mr-2" size={20} />
              Chargement...
            </div>
          )}

          {error && <p className="text-red-600">{error}</p>}

          {!isLoading && !error && upcoming.length === 0 && (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-600">
              <p className="mb-4">Aucun tournoi à venir dans ton programme.</p>
              <button
                onClick={() => navigate('/calendar')}
                className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition"
              >
                Parcourir le calendrier
              </button>
            </div>
          )}

          {!isLoading && !error && upcoming.length > 0 && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                <div className="bg-white rounded-lg shadow p-4">
                  <p className="text-xs text-gray-500">Tournois à venir</p>
                  <p className="text-2xl font-bold text-gray-800">{upcoming.length}</p>
                  <p className="text-xs text-gray-500">sur {days.length} jour{days.length > 1 ? 's' : ''}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <p className="text-xs text-gray-500">Prochain tournoi</p>
                  <p className="font-semibold text-gray-800 break-words">{next.tournamentTitle}</p>
                  <p className="text-xs text-gray-500">
                    {formatDay(next.tournamentDate)}
                    {next.tournamentTime ? ` · ${String(next.tournamentTime).slice(0, 5)}` : ''}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Wallet size={14} />
                    Budget estimé
                  </p>
                  <p className="text-2xl font-bold text-purple-700">{formatEuros(budget.total)}</p>
                  <p className="text-xs text-gray-500">
                    buy-ins × entrées prévues (1 par défaut)
                    {budget.unknown > 0 && ` · ${budget.unknown} sans buy-in connu`}
                  </p>
                </div>
              </div>

              <div className="space-y-5">
                {days.map(([date, items]) => (
                  <section key={date} className="bg-white rounded-lg shadow overflow-hidden">
                    <h2 className="px-4 py-2 bg-gray-100 text-sm font-semibold text-gray-700 capitalize">
                      {formatDay(date)}
                    </h2>
                    <ul className="divide-y divide-gray-100">{items.map(renderRow)}</ul>
                  </section>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MySchedulePage;
